import { useEffect } from 'react'

/**
 * The ROI calculator's arithmetic, recovered and rewritten in React.
 *
 * One page uses it — `/roi-calculator`, eleven sliders and a period toggle.
 *
 * ── Where this came from ──────────────────────────────────────────────
 *
 * The page is a Webflow `w-embed w-script` block, and the porter kept every
 * embed's `<style>` while dropping every embed's `<script>`. So the markup
 * shipped with 44 element ids wired to nothing: the sliders dragged and every
 * figure on the page stayed at its hard-coded value. It had been a static
 * mock-up of a calculator for the life of the port.
 *
 * The original was recovered from the Webflow staging origin
 * (`singulate-2-0.webflow.io`, the same one `check-parity` reads — `www` sits
 * behind a Cloudflare challenge, staging does not) and is reproduced here
 * verbatim in behaviour. **Every threshold, rate and formula below is the
 * original's**; only the shape is different — named constants instead of
 * `eStops()`, one `read`/`write` pair instead of 30 `getElementById` calls,
 * and listeners that clean up instead of a bare IIFE.
 *
 * Driven from `WebflowBinder` rather than by rewriting the markup, for the
 * same reason as `useWebflowTabs` and `useInViewReveal`: the ported DOM stays
 * exactly as captured and keeps its own ids.
 *
 * The capture drove the period buttons with inline `onclick="sSetPeriod(…)"`,
 * which JSX drops, so those are wired here too — no `window` global.
 */

/** Slider index → real quantity. The original's `eStops`/`cStops`/… tables. */
const EMAIL_STOPS = [1000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000, 10000000]
const CREDIT_STOPS = [1000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000]
const VOLUME_STOPS = [1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2000000, 5000000]
const REVENUE_STOPS = [5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000]

/** The click-rate slider is non-linear at the bottom: 0.25%, 0.5%, then ×0.5. */
const clickRate = (i: number) => (i === 0 ? 0.25 : i === 1 ? 0.5 : i * 0.5)

const num = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  : n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`
  : String(n)

const money = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
  : n >= 1000 ? `$${Math.round(n / 1000)}K`
  : `$${Math.round(n)}`

/* ── Pricing. These tiers are the business's, not the design system's. ── */
/** $18k covers the first 50k emails, then a per-email rate that tapers. */
const emailPrice = (q: number) =>
  q <= 50000 ? 18000 : 18000 + (q - 50000) * (q <= 1_000_000 ? 0.02 : q <= 2_000_000 ? 0.015 : 0.01)
/** First 10k research credits are free. */
const creditPrice = (q: number) =>
  q <= 10000 ? 0 : (q - 10000) * (q <= 500000 ? 0.04 : q <= 1_000_000 ? 0.03 : 0.02)
/** First 6 templates free, then $400 each. */
const templatePrice = (q: number) => (q <= 6 ? 0 : (q - 6) * 400)

/** Hourly rate the "time saved" figures are costed at. */
const HOURLY_RATE = 150

type Period = 'annual' | 'quarterly' | 'monthly'
const PERIODS: Period[] = ['annual', 'quarterly', 'monthly']
const LABEL: Record<Period, string> = {
  annual: 'Annual',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
}
/** Periods per year — what an annual figure is divided by. */
const DIVISOR: Record<Period, number> = { annual: 1, quarterly: 4, monthly: 12 }
/** Months in the period — what a monthly figure is multiplied by. */
const MONTHS: Record<Period, number> = { annual: 12, quarterly: 3, monthly: 1 }

const SLIDERS = [
  's-emails', 's-credits', 's-templates', 's-vol', 's-open',
  's-click', 's-revenue', 's-hours', 's-open-lift', 's-click-lift', 's-time-saved',
] as const

export function useRoiCalculator(route: string) {
  useEffect(() => {
    const el = (id: string) => document.getElementById(id)
    if (!el('s-emails')) return

    const read = (id: string) => Number((el(id) as HTMLInputElement | null)?.value ?? 0)
    const write = (id: string, text: string) => {
      const node = el(id)
      if (node) node.textContent = text
    }

    let period: Period = 'annual'

    /*
     * The "avg customer" tick under each lift slider. Positioned in percent
     * from the track's usable width, allowing for the 18px thumb — so it has
     * to be recomputed on resize, and skipped while the element has no width
     * (the page can render before layout settles).
     */
    const notch = (value: number, min: number, max: number, track: HTMLElement) => {
      const THUMB = 18
      const w = track.offsetWidth
      if (!w) return null
      return `${((THUMB / 2 + ((value - min) / (max - min)) * (w - THUMB)) / w * 100).toFixed(2)}%`
    }
    const positionNotches = () => {
      for (const [slider, marker, value, min, max] of [
        ['s-open-lift', 's-open-notch', 50, 5, 200],
        ['s-click-lift', 's-click-notch', 100, 5, 700],
        ['s-time-saved', 's-time-notch', 7, 1, 40],
      ] as const) {
        const track = el(slider)
        const mark = el(marker)
        if (!track || !mark) continue
        const left = notch(value, min, max, track)
        if (left) mark.style.left = left
      }
    }

    const update = () => {
      const emails = EMAIL_STOPS[Math.min(read('s-emails'), EMAIL_STOPS.length - 1)]
      const credits = CREDIT_STOPS[Math.min(read('s-credits'), CREDIT_STOPS.length - 1)]
      const templates = read('s-templates')
      const volume = VOLUME_STOPS[Math.min(read('s-vol'), VOLUME_STOPS.length - 1)]
      const pipeline = REVENUE_STOPS[Math.min(read('s-revenue'), REVENUE_STOPS.length - 1)]
      const openRate = read('s-open')
      const clicks = clickRate(read('s-click'))
      const openLift = read('s-open-lift')
      const clickLift = read('s-click-lift')
      const hours = read('s-hours')
      const savedHours = read('s-time-saved')

      /* Slider read-outs. */
      write('s-emails-val', num(emails))
      write('s-credits-val', num(credits))
      write('s-templates-val', String(templates))
      write('s-vol-val', num(volume))
      write('s-open-val', `${openRate}%`)
      write('s-click-val', `${clicks.toFixed(clicks < 1 ? 2 : 1)}%`)
      write('s-revenue-val', money(pipeline))
      write('s-hours-val', `${hours} hrs`)
      write('s-open-lift-val', `+${openLift}%`)
      write('s-click-lift-val', `+${clickLift}%`)
      write('s-time-saved-val', `${savedHours} hrs`)

      /* Price of the plan the top three sliders describe. */
      const savedPerCampaign = savedHours * HOURLY_RATE
      write('s-time-hint', `${money(savedPerCampaign)} saved per campaign · at $${HOURLY_RATE}/hr`)
      const annualPrice = Math.round(
        emailPrice(emails) + creditPrice(credits) + templatePrice(templates),
      )
      write('s-annual-price', money(annualPrice))
      write('s-monthly-price', money(Math.round(annualPrice / 12)))
      write('s-breakdown', `${num(emails)} emails · ${num(credits)} credits · ${templates} templates`)

      /* The summary, restated in whichever period the toggle selects. */
      const label = LABEL[period]
      const newOpen = +(openRate * (1 + openLift / 100)).toFixed(1)
      const newClick = +(clicks * (1 + clickLift / 100)).toFixed(2)
      const addedPerPeriod = Math.round(pipeline * (clickLift / 100)) * MONTHS[period]
      const timeSaved = Math.round((savedPerCampaign * 12) / DIVISOR[period])
      const cost = Math.round(annualPrice / DIVISOR[period])
      const totalValue = addedPerPeriod + timeSaved

      write('s-new-open', `${newOpen}%`)
      write('s-new-click', `${newClick < 1 ? newClick.toFixed(2) : newClick.toFixed(1)}%`)
      write('s-pipeline-lbl', `${label} pipeline added`)
      write('s-pipeline', money(addedPerPeriod))
      write('s-time-lbl', `${label} time savings`)
      write('s-time-savings', money(timeSaved))
      write('s-cost-lbl', `${label} cost`)
      write('s-cost', money(cost))
      write('s-roi-lbl', `${label} ROI`)
      write('s-roi-val', `${cost > 0 ? Math.round(totalValue / cost) : 0}x`)
      write('s-roi-sub', `${money(totalValue)} total value on ${money(cost)} investment`)

      positionNotches()
    }

    const cleanups: Array<() => void> = []
    for (const id of SLIDERS) {
      const node = el(id)
      if (!node) continue
      node.addEventListener('input', update)
      cleanups.push(() => node.removeEventListener('input', update))
    }
    for (const p of PERIODS) {
      const btn = el(`s-btn-${p}`)
      if (!btn) continue
      const onClick = () => {
        period = p
        for (const other of PERIODS) {
          el(`s-btn-${other}`)?.classList.toggle('active', other === p)
        }
        update()
      }
      btn.addEventListener('click', onClick)
      cleanups.push(() => btn.removeEventListener('click', onClick))
    }
    window.addEventListener('resize', positionNotches)
    cleanups.push(() => window.removeEventListener('resize', positionNotches))

    // The track has no width until layout settles, so the notches need a
    // second pass; the capture used the same 150ms.
    const t = window.setTimeout(positionNotches, 150)
    cleanups.push(() => window.clearTimeout(t))

    update()
    return () => {
      for (const off of cleanups) off()
    }
  }, [route])
}
